import { BackOfficeDataServiceAPI } from '@API/data-services/back-office-data-service'
import { useAppDispatch, RootState } from '@AppState/redux-store'
import { useAuth0, withAuth0, WithAuth0Props } from '@auth0/auth0-react'
import { AppConfigLoader } from '@Components/app-config'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Outlet, useLocation, useRevalidator } from 'react-router-dom'
import { UserActions } from '@AppState/users/users-slice'
import { GambytJwtPayload } from '@Components/users/types'
import { jwtDecode } from 'jwt-decode'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { useQuery } from '@tanstack/react-query'
import { getLogoutParams } from '@Components/util/utils'
import { UsersServiceAPI } from '@API/data-services/users-service'
import { CustomerServiceAPI } from '@API/data-services/customer-service'

export const MainApp = withAuth0((auth0Props: WithAuth0Props) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const revalidator = useRevalidator()
  const appConfig = useSelector((state: RootState) => state.appConfig.config)
  const { activeCustomerId, currentUser } = useSelector((state: RootState) => state.users)

  const { data: extraUserData } = useQuery({
    queryKey: [`${activeCustomerId}-extra-user-data`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      const { userScopes, enabledFeatures, isGambytAdmin } =
        await UsersServiceAPI.getCurrentUserScopes()
      const metadata = await UsersServiceAPI.getCurrentUserMeta()

      return { scopes: userScopes, enabledFeatures, isGambytAdmin, metadata }
    },
    enabled: !!currentUser,
  })

  const { data: customerMetadata } = useQuery({
    queryKey: [`${activeCustomerId}-metadata`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return CustomerServiceAPI.getCustomerConfig()
    },
    enabled: !!currentUser,
  })

  const { isLoading: authLoading, user, error } = useAuth0()

  const gambytUser = useMemo(() => {
    return user ? { authProviderId: user.sub!, email: user.email!, name: user.name! } : null
  }, [user])

  useEffect(() => {
    // Start the flow for accepting an organization invite
    if (location.search.includes('invitation')) {
      auth0Props.auth0.loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } })
    }
  }, [auth0Props.auth0, location.search])

  useEffect(() => {
    if (authLoading) {
      return
    }

    if (!gambytUser) {
      return
    }

    const fetchToken = async () => {
      try {
        const token = await auth0Props.auth0.getAccessTokenSilently()
        const decoded = jwtDecode<GambytJwtPayload>(token)

        const currentCustomer = decoded.orgs.find((o) => o.id === decoded.org_id)!

        dispatch(
          UserActions.setCurrentUser({
            ...gambytUser,
            isGambytAdmin: false,
            scopes: [], // we need to do a seperate call to get the user's scopes
            enabledFeatures: [],
            metadata: {}, // we need to do a seperate call to get the user's metadata
            customers: decoded.orgs,
            authProviderId: decoded.sub!,
          }),
        )
        dispatch(UserActions.setActiveCustomer({ customer: currentCustomer }))
      } catch (_ignored) {
        auth0Props.auth0.logout(getLogoutParams())
      }
    }

    fetchToken()
  }, [auth0Props.auth0, authLoading, dispatch, gambytUser])

  useEffect(() => {
    if (extraUserData) {
      dispatch(UserActions.setUserScopes(extraUserData.scopes))
      dispatch(UserActions.setEnabledFeatures(extraUserData.enabledFeatures))
      dispatch(UserActions.setIsGambytAdmin(extraUserData.isGambytAdmin))
      dispatch(UserActions.setUserMetadata(extraUserData.metadata))
    }
  }, [currentUser, dispatch, extraUserData])

  useEffect(() => {
    if (customerMetadata) {
      dispatch(UserActions.setCustomerMetadata(customerMetadata))
    }
  }, [currentUser, customerMetadata, dispatch, extraUserData])

  const appReady = useCallback(() => {
    const isReady = appConfig && !authLoading && !error && gambytUser && activeCustomerId

    if (isReady) {
      BackOfficeDataServiceAPI.configureApis(
        {
          basePath: appConfig.backOfficeDataServiceUrl,
          customerId: activeCustomerId,
        },
        auth0Props.auth0,
      )
    }

    return isReady
  }, [activeCustomerId, appConfig, auth0Props.auth0, authLoading, error, gambytUser])

  useEffect(() => {
    if (appReady()) {
      // Needed to support deep linking - on initial render we don't have a user set and the path won't match until we know they have permissions
      revalidator.revalidate()
    }
  }, [appReady, revalidator])

  if (!appReady()) {
    return (
      <div>
        <AppConfigLoader />
      </div>
    )
  }

  return <Outlet />
})
